/**
 * delivery-row-wording: the ONE place that words a delivery row (CC 0450). The chip, the subject's reason line, the
 * drawer header tail and the notice above the body all come from rowWording(status, contentSource). No other code
 * in the delivery log decides what a row claims.
 *
 * What each status may claim:
 *   queued, suppressed, cancelled → never sent      (only these may say "not sent" / "never sent")
 *   sent, delivered, bounced      → sent            (only these may say "sent")
 *   failed, unknown               → no claim either way: a provider attempt may have happened without a stored copy.
 * A status this screen doesn't know reads as "unknown".
 */
import { t as i18nT } from "../../lib/i18n";

export const ROW_STATUSES = ["queued", "sent", "delivered", "bounced", "failed", "suppressed", "cancelled", "unknown"] as const;
export type RowStatus = (typeof ROW_STATUSES)[number];
export type RowClaim = "never-sent" | "sent" | "none";
export type ChipTone = "warn" | "neutral" | "danger" | "success";
export interface RowWording {
  status: RowStatus;
  claim: RowClaim;
  /** The row's content chip. */
  chip: { label: string; tone: ChipTone };
  /** The drawer header's tail after the channel name ("· sent 10:02", "· failed", …). */
  header: string;
  /** The line under the subject (suppressed and cancelled only), when the reason is known. */
  reasonLine: string | null;
  /** The notice above the body. Template rows only: a dispatch row shows its stored copy and needs none. */
  notice: { tone: "warning" | "neutral"; title: string | null; body: string } | null;
}

const KNOWN = new Set<string>(ROW_STATUSES);
export const rowStatusOf = (s: unknown): RowStatus => (typeof s === "string" && KNOWN.has(s) ? (s as RowStatus) : "unknown");
const CLAIM: Record<RowStatus, RowClaim> = {
  queued: "never-sent", suppressed: "never-sent", cancelled: "never-sent",
  sent: "sent", delivered: "sent", bounced: "sent",
  failed: "none", unknown: "none",
};
const sentence = (clause: string): string => clause.charAt(0).toUpperCase() + clause.slice(1) + ".";

/** `reason` is a lowercase clause from delivery-data's stopReasonOf ("the booking was cancelled or moved"), or empty
 *  when none is known. Only suppressed and cancelled use it. */
export function rowWording(status: unknown, contentSource: "dispatch" | "template" | undefined | null,
  opts: { reason?: string | null; sentAt?: string | null; plannedAt?: string | null } = {}): RowWording {
  const s = rowStatusOf(status);
  const template = contentSource === "template";
  const reason = (opts.reason ?? "").trim();
  const at = (v?: string | null) => (v ? " " + v : "");
  const L = {
    notSentYet: i18nT("messaging.rowWording.chipNotSentYet", "Not sent yet"),
    notSent: i18nT("messaging.rowWording.chipNotSent", "Not sent"),
    failed: i18nT("messaging.rowWording.chipFailed", "Failed"),
    unknown: i18nT("messaging.rowWording.chipMayNotHaveBeenSent", "May not have been sent"),
    fromTemplate: i18nT("messaging.rowWording.chipFromTemplate", "Content shown from the template"),
    asSent: i18nT("messaging.deliveryLog.asSent", "As sent"),
    bounced: i18nT("messaging.rowWording.chipBounced", "Bounced"),
  };
  const H = {
    sent: i18nT("messaging.deliveryLog.sent", "· sent") + at(opts.sentAt),
    notSent: i18nT("messaging.deliveryLog.notSentLower", "· not sent"),
    notSentYet: opts.plannedAt ? i18nT("messaging.deliveryLog.plannedForLower", "· planned") + at(opts.plannedAt) : i18nT("messaging.deliveryLog.notSentYet", "· not sent yet"),
    failed: i18nT("messaging.rowWording.headerFailed", "· failed"),
    unknown: i18nT("messaging.deliveryLog.mayNotHaveBeenSentLower", "· may not have been sent"),
  };
  const base = { status: s, claim: CLAIM[s] } as const;
  switch (s) {
    case "queued":
      return { ...base, chip: { label: L.notSentYet, tone: "warn" }, header: H.notSentYet, reasonLine: null,
        notice: template ? { tone: "warning", title: L.notSentYet, body: i18nT("messaging.rowWording.noticeQueued", "Not sent yet. Showing the template, as it will be filled in when sent.") } : null };
    case "suppressed":
      return { ...base, chip: { label: L.notSent, tone: "warn" }, header: H.notSent, reasonLine: reason ? sentence(reason) : null,
        notice: template ? { tone: "warning", title: L.notSent, body: reason
          ? i18nT("messaging.rowWording.noticeSuppressedReason", "This message was never sent ({reason}). Showing the template.").replace("{reason}", reason)
          : i18nT("messaging.rowWording.noticeSuppressed", "This message was never sent. Showing the template.") } : null };
    case "cancelled":
      return { ...base, chip: { label: L.notSent, tone: "warn" }, header: H.notSent,
        reasonLine: reason ? sentence(reason) : i18nT("messaging.rowWording.reasonCancelled", "Cancelled before it was sent."),
        notice: template ? { tone: "warning", title: L.notSent, body: reason
          ? i18nT("messaging.rowWording.noticeCancelledReason", "Cancelled before it was sent ({reason}). Showing the template.").replace("{reason}", reason)
          : i18nT("messaging.rowWording.noticeCancelled", "Cancelled before it was sent. Showing the template.") } : null };
    case "failed":
      return { ...base, chip: { label: L.failed, tone: "danger" }, header: H.failed, reasonLine: null,
        notice: template ? { tone: "warning", title: L.failed, body: i18nT("messaging.rowWording.noticeFailed", "Sending failed. The exact content sent, if any, wasn't stored, so the template is shown.") } : null };
    case "unknown":
      return { ...base, chip: { label: L.unknown, tone: "warn" }, header: H.unknown, reasonLine: null,
        notice: template ? { tone: "warning", title: L.unknown, body: i18nT("messaging.rowWording.noticeUnknown", "We can't confirm this was sent. The exact content wasn't stored, so the template is shown.") } : null };
    case "bounced":
      return { ...base, chip: { label: L.bounced, tone: "danger" }, header: H.sent, reasonLine: null,
        notice: template ? { tone: "warning", title: L.bounced, body: i18nT("messaging.rowWording.noticeBounced", "Sent, then bounced. The exact content wasn't stored, so the template is shown.") } : null };
    case "sent":
    case "delivered":
    default:
      return { ...base, chip: template ? { label: L.fromTemplate, tone: "neutral" } : { label: L.asSent, tone: "success" }, header: H.sent, reasonLine: null,
        notice: template ? { tone: "neutral", title: null, body: i18nT("messaging.rowWording.noticeHistorical", "Sent before sent copies were stored. Showing the template.") } : null };
  }
}
